import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

const ScreenerQuotaSchema = z.object({
  available: z.number(),
  remains: z.number(),
  blocked: z.boolean(),
});

const responseSchema = z.object({
  message: z.string(),
  data: ScreenerQuotaSchema,
});

export type ScreenerQuotaData = z.infer<typeof ScreenerQuotaSchema>;

export const getScreenerQuota = async ({
  lang,
  token,
}: {
  lang: Lang;
  token: string;
}): Promise<ScreenerQuotaData> => {
  const endpoint = "/screener-quota";
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: {
      "Accept-Language": lang,
      "X-User-Profile": token,
    },
    cache: "no-cache",
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return responseSchema.parse(await res.json()).data;
};

export const useScreenerQuota = () => {
  const { lang } = useLang();
  const { backendToken } = useAuth();

  return useQuery(
    ["screener-quota", lang],
    () => getScreenerQuota({ lang, token: backendToken ?? "" }),
    {
      enabled: !!backendToken,
    }
  );
};
